import dayjs from 'dayjs';

/**
 * Non-React helpers for tz-aware date formatting. Use these in services,
 * print code, and other non-component code paths. React components should
 * prefer the `useRestaurantTimeZone` hook so they re-render when the
 * setting changes.
 *
 * Requires `dayjs.extend(utc)` and `dayjs.extend(timezone)` to be loaded
 * at app boot (see frontend/src/index.tsx).
 */

export const DEFAULT_TIMEZONE = 'America/Mexico_City';

export const formatInTz = (
  date: string | Date | dayjs.Dayjs,
  tz: string = DEFAULT_TIMEZONE,
  pattern: string = 'DD/MM/YYYY HH:mm',
): string => dayjs(date).tz(tz).format(pattern);

/**
 * Format a date-only API field (`dailyBreakdown[].date`, `DailyMenu.date`...).
 * The backend sends the calendar day itself at midnight UTC, so it isn't an
 * instant: converting it to the restaurant's tz lands on the day before in any
 * tz behind UTC. Real timestamps still go through `formatInTimeZone`/`formatInTz`.
 * A missing value gives '' instead of dayjs' fallback to the current date.
 */
export const formatDateOnly = (
  date: string | null | undefined,
  pattern: string = 'DD/MM/YYYY',
): string => (date ? dayjs.utc(date).format(pattern) : '');

/**
 * A date-only API field as a DatePicker value: the same calendar day in the
 * browser's tz, so saving the form sends back the day it loaded.
 */
export const parseDateOnly = (date: string): dayjs.Dayjs =>
  dayjs(formatDateOnly(date, 'YYYY-MM-DD'));

/**
 * Serialize a Dayjs to `yyyy-MM-dd` using its own calendar components — no
 * timezone math, no `.format()` call (which the project lint rule restricts).
 * Use this when sending date-only strings to the backend: the server interprets
 * them as local dates in the restaurant's timezone, then converts to UTC. This
 * is what keeps Expense creation and P&L bucketing consistent even when the
 * support user's browser timezone differs from the restaurant's.
 */
export const toIsoDate = (d: dayjs.Dayjs): string => {
  const yyyy = d.year();
  const mm = String(d.month() + 1).padStart(2, '0');
  const dd = String(d.date()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const startOfDayInTz = (tz: string = DEFAULT_TIMEZONE): dayjs.Dayjs =>
  dayjs().tz(tz).startOf('day');

export const endOfDayInTz = (tz: string = DEFAULT_TIMEZONE): dayjs.Dayjs =>
  dayjs().tz(tz).endOf('day');

/**
 * Start of the current week (Monday) in the given timezone.
 *
 * dayjs `startOf('week')` defaults to Sunday-anchored. We treat Monday as
 * the first business day, so on Sunday step back six days to the previous
 * Monday; Mon–Sat go forward one day from the Sunday-anchored week start.
 */
export const startOfWeekInTz = (tz: string = DEFAULT_TIMEZONE): dayjs.Dayjs => {
  const today = dayjs().tz(tz).startOf('day');
  return today.day() === 0
    ? today.subtract(6, 'day')                  // Sunday → previous Monday
    : today.startOf('week').add(1, 'day');      // Mon–Sat → this week's Monday
};

export const toTz = (
  date: string | Date | dayjs.Dayjs,
  tz: string = DEFAULT_TIMEZONE,
): dayjs.Dayjs => dayjs(date).tz(tz);
